/**
 * Helpers puros das rotas de motorista.
 * Sem React/serviços, para permitir teste unitário (vitest).
 */
import type { DriverRouteStop, RouteStopType } from '@/types/driverRoute';

/**
 * Converte o texto digitado na busca por pedido de venda no id enviado à API.
 *
 * Aceita o número puro e as formas em que o pedido aparece no sistema ("#29671",
 * "Pedido #29671"), porque é comum colar o rótulo da parada. Devolve `undefined`
 * quando não há um id utilizável — aí o filtro simplesmente não é aplicado.
 */
export const parseOrderIdSearch = (raw: string): number | undefined => {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return undefined;
  const value = Number(digits);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
};

// ─── Tipos de parada ─────────────────────────────────────────────

export interface StopRef {
  route_stop_type: RouteStopType;
  order_id?: number | null;
  goods_pickup_orders_id?: number | null;
  delivery_order_id?: number | null;
  goods_delivery_orders_id?: number | null;
}

/** Id da entidade de origem da parada (pedido, retirada, convite ou ordem de entrega). */
export const stopRefId = (stop: StopRef): number | null | undefined => {
  switch (stop.route_stop_type) {
    case 'E':
      return stop.order_id;
    case 'R':
      return stop.goods_pickup_orders_id;
    case 'D':
      return stop.delivery_order_id;
    case 'O':
      return stop.goods_delivery_orders_id;
  }
};

/** Chave estável da parada no formulário de rota (casa parada salva ↔ parada da tela). */
export const getStopKey = (stop: StopRef): string => `${stop.route_stop_type}-${stopRefId(stop) ?? 'unknown'}`;

/** Rótulo do tipo de parada (badge do Gerenciar Rotas). */
export const stopTypeLabel = (type: RouteStopType): string => {
  switch (type) {
    case 'E':
      return 'Entrega';
    case 'R':
      return 'Retirada';
    case 'D':
      return 'Convite';
    case 'O':
      return 'Ordem de Entrega';
  }
};

/** Rótulo do tipo na tela do motorista (a ordem de entrega aparece como "Entrega avulsa"). */
export const driverStopTypeLabel = (type: RouteStopType): string =>
  type === 'O' ? 'Entrega avulsa' : stopTypeLabel(type);

/** Referência curta da parada: "Pedido #N", "Retirada #N", "Convite #N", "Ordem de Entrega #N". */
export const stopRefLabel = (stop: StopRef): string => {
  const id = stopRefId(stop) ?? '?';
  switch (stop.route_stop_type) {
    case 'E':
      return `Pedido #${id}`;
    case 'R':
      return `Retirada #${id}`;
    case 'D':
      return `Convite #${id}`;
    case 'O':
      return `Ordem de Entrega #${id}`;
  }
};

/** Cor do badge do tipo: entregas (E/D/O) em azul, retirada em âmbar. */
export const stopTypeBadgeVariant = (type: RouteStopType): 'info' | 'warning' =>
  type === 'R' ? 'warning' : 'info';

/** Tipos confirmados pelo modal "Confirmar Entrega" (contato + documento + fotos). */
export const usesDeliveryConfirmation = (type: RouteStopType): boolean =>
  type === 'E' || type === 'D' || type === 'O';

// ─── Rastreamento ao vivo e entrega suspensa (RN-ROTA-45 a 49) ───

/** Parada de pedido entregue pela frota própria direto ao cliente (tipo E sem transportadora). */
export const isOwnFleetDeliveryStop = (stop: Pick<DriverRouteStop, 'route_stop_type' | 'order_id' | 'shipping_provider_id'>): boolean =>
  stop.route_stop_type === 'E' && !!stop.order_id && !(stop.shipping_provider_id && stop.shipping_provider_id > 0);

/** Parada fechada: entregue ou suspensa (deixa de ser pendente). */
export const isStopClosed = (stop: Pick<DriverRouteStop, 'is_delivered' | 'suspended_at'>): boolean =>
  stop.is_delivered || !!stop.suspended_at;

/** Parada que está "em rota" (link ativo). A rota tem no máximo uma. */
export const activeLiveTrackingStop = (stops: DriverRouteStop[] | undefined): DriverRouteStop | undefined =>
  stops?.find((s) => s.live_tracking?.status === 'A' && !isStopClosed(s));

/** Motivos rápidos da entrega não realizada (o texto livre fica em "Outro"). */
export const SUSPEND_REASONS = ['Cliente ausente', 'Endereço não encontrado', 'Cliente recusou o recebimento'] as const;
